/**
 * @module shared/horizontalScrollGuard
 *
 * Prevents macOS trackpad horizontal overscroll from triggering browser
 * back/forward navigation when a horizontally-scrollable container is at an edge.
 *
 * Single source of truth for every horizontal-scroll table/data-view. Originally
 * proven on the HubTable (`/audiences/:id`); HubTable's scroll-guard module now
 * re-exports from here so all scrollable tables behave identically.
 *
 * Two layers, used together:
 *   1. CSS `overscroll-x-none` ({@link HORIZONTAL_SCROLL_GUARD_CLASS}) — the primary
 *      defense; stops overscroll from chaining to history navigation.
 *   2. A non-passive `wheel` listener ({@link attachHorizontalScrollGuard}) — belt
 *      and suspenders for macOS, where the CSS alone can still let a fast edge swipe
 *      through. React's synthetic onWheel is passive, so preventDefault() there is a
 *      no-op; this attaches a real non-passive listener.
 */

/** Tailwind classes to add to a horizontal scroll container to block overscroll-nav. */
export const HORIZONTAL_SCROLL_GUARD_CLASS = 'overscroll-x-none overscroll-y-contain';

/** Returns true when a horizontal wheel delta would overscroll past the container edges. */
export function shouldBlockHorizontalOverscrollWheel(
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
  deltaX: number,
): boolean {
  if (deltaX === 0) return false;
  const atLeft = scrollLeft <= 0;
  const atRight = scrollLeft + clientWidth >= scrollWidth - 1;
  return (atLeft && deltaX < 0) || (atRight && deltaX > 0);
}

/**
 * Attach a non-passive wheel listener so preventDefault() works on macOS.
 * React's synthetic onWheel is passive in modern browsers. Returns a cleanup fn.
 */
export function attachHorizontalScrollGuard(el: HTMLElement): () => void {
  const onWheel = (e: WheelEvent) => {
    const { scrollWidth, clientWidth, scrollLeft } = el;
    if (scrollWidth <= clientWidth) return;

    const absX = Math.abs(e.deltaX);
    const absY = Math.abs(e.deltaY);

    // Horizontal trackpad swipe (or shift+scroll).
    if (absX > absY) {
      if (shouldBlockHorizontalOverscrollWheel(scrollLeft, scrollWidth, clientWidth, e.deltaX)) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    // Diagonal scroll at horizontal edges — still triggers browser back on macOS.
    if (absX > 0 && shouldBlockHorizontalOverscrollWheel(scrollLeft, scrollWidth, clientWidth, e.deltaX)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  el.addEventListener('wheel', onWheel, { passive: false });
  return () => el.removeEventListener('wheel', onWheel);
}
